import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
  Label,
  Input,
  Button,
} from "@/common/components/ui";
import {
  ShieldAlert,
  Mail,
  Lock,
  Loader2,
  ArrowRight,
  ShieldCheck,
  Landmark,
  Eye,
  EyeOff,
} from "lucide-react";
import { useUser } from "@/context/useUser";
import { notify } from "@/common/components/common/Notification";
import FinEaseHomeRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";
/**
 * AdminLogin Page
 * A dedicated, premium login page for administrative users.
 * Uses Email/Username and Password authentication.
 */
const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useUser();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      notify.error("Please enter both email and password");
      return;
    }

    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (result) {
        notify.success("Login Successful");
        navigate(FinEaseHomeRoutes.ADMIN_DASHBOARD);
      } else {
        notify.error("Invalid credentials");
      }
    } catch (error: any) {
      notify.error(error.message ?? "Invalid credentials");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#f8fafc]">
      {/* Background Decoration */}
      <div className="pointer-events-none absolute left-0 top-0 h-full w-full overflow-hidden">
        <div className="absolute right-[-5%] top-[-10%] h-[40%] w-[40%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-5%] h-[40%] w-[40%] rounded-full bg-secondary/5 blur-[120px]" />
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03]" />
      </div>

      <div className="animate-scale-in relative z-10 w-full max-w-md p-6">
        {/* Brand Logo */}
        <div className="mb-10 flex flex-col items-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary shadow-xl shadow-secondary/20">
            <Landmark className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-display text-center text-3xl font-bold tracking-tight text-secondary">
            FinEase <span className="text-primary">Admin</span>
          </h1>
          <p className="mt-2 text-sm font-medium text-slate-500">
            Internal Management Systems
          </p>
        </div>

        <Card className="overflow-hidden border-none bg-white/80 shadow-2xl shadow-slate-200/50 backdrop-blur-xl">
          <div className="h-1.5 w-full bg-gradient-to-r from-primary via-secondary to-primary" />

          <CardHeader className="space-y-1 pb-8">
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-100">
                <ShieldAlert className="h-4 w-4 text-orange-600" />
              </div>
              <span className="text-xs font-bold uppercase tracking-wider text-orange-600">
                Secure Access
              </span>
            </div>
            <CardTitle className="text-2xl font-bold text-secondary">
              Sign In
            </CardTitle>
            <CardDescription className="text-slate-500">
              Enter your credentials to access the administration portal.
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className="flex items-center gap-2 text-sm font-semibold text-secondary"
                >
                  <Mail className="h-3.5 w-3.5" /> Email or Username
                </Label>
                <div className="group relative">
                  <Input
                    id="email"
                    type="text"
                    placeholder="admin@thefinease.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="h-12 border-slate-200 bg-slate-50/50 pl-4 transition-all focus:border-primary focus:ring-primary/20"
                    disabled={isLoading}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label
                    htmlFor="password"
                    className="flex items-center gap-2 text-sm font-semibold text-secondary"
                  >
                    <Lock className="h-3.5 w-3.5" /> Password
                  </Label>
                  <Button
                    variant="link"
                    className="h-auto px-0 text-xs font-medium text-primary hover:text-primary/80"
                  >
                    Forgot Password?
                  </Button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="h-12 border-slate-200 bg-slate-50/50 pl-4 pr-12 transition-all focus:border-primary focus:ring-primary/20"
                    disabled={isLoading}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-12 w-12 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    onClick={() => setShowPassword(prev => !prev)}
                    tabIndex={-1}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <Button
                type="submit"
                className="group h-12 w-full rounded-xl bg-secondary font-bold text-white shadow-lg shadow-secondary/20 transition-all hover:bg-secondary/95"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                ) : (
                  <>
                    Access Dashboard
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>

          <CardFooter className="flex flex-col items-center space-y-3 border-t border-slate-100 bg-slate-50/50 py-5">
            <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-widest text-slate-400">
              <ShieldCheck className="h-3 w-3 text-secondary" />
              Protected by Enterprise Security
            </div>
          </CardFooter>
        </Card>

        <p className="mt-8 text-center text-xs font-medium text-slate-400">
          &copy; {new Date().getFullYear()} FinEase Capital Management.
          Unauthorized access is strictly prohibited.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
